import React, { useState, useMemo, useRef, useEffect, memo, useCallback } from 'react';
import { PlusIcon, ChevronDownIcon, ChevronRightIcon, PencilIcon, ClipboardDocumentListIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import TanstackTable from '../../Common/TanstackTable/TanstackTable';
import api from '../../../middleware/api';
import API from '../../../routes/APIEndpoints';
import Pagination from '../../Common/DaisyTable/Pagination/Pagination';
import { formatDate } from '../../../utils/format.utils';
import LoadingSpinner from '../../Common/Loading/LoadingSpinner';
import CapsuleDetails from './CapsuleDetails';
import { toast } from 'react-toastify';
import downloadPDF from '../../../utils/pdf.utils';
import TooltipLabelPDF from '../../PDF/TooltipLabelPDF';
import OrderStatus from '../../Common/OrderStatus/OrderStatus';
import RadixPopover from '../../Common/RadixPopover/RadixPopover';
import { Loader, Loader2 } from 'lucide-react';


const getStatusFilter = (status) => {
    switch (status) {
        case 'with-order':
            return 'Avec commande';
        case 'without-order':
            return 'Sans commande';
        case 'multiple-orders':
            return 'Multiples commandes';
        case 'pending':
            return 'En attente';
        case 'processing':
            return 'En cours';
        case 'on-hold':
            return 'En attente de vérification';
        case 'completed':
            return 'Terminé';
        case 'cancelled':
            return 'Annulé';
        case 'refunded':
            return 'Remboursé';
        case 'failed':
            return 'Échoué';
        case 'trash':
            return 'Corbeille';
        case 'livre':
            return 'Livrée';
        case 'expedie':
            return 'Envoyée';
        default:
            return status;
    }
}

// Debounce hook for search
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

const sortDilutions = (dilutions) => {
    const dilutionSorting = ['A', 'R', 'S', 'E', 'T-08E', 'T-08P', 'T-10E', 'T-10P', 'T-12E', 'T-12P', 'Sans']
    const reducedDilutions = dilutions.reduce((acc, item) => {
        if (!acc.includes(item)) {
            acc.push(item)
        }
        return acc
    }, [])


    const sortedDilutions = reducedDilutions.sort((a, b) => {
        const indexA = dilutionSorting.indexOf(a)
        const indexB = dilutionSorting.indexOf(b)
        // Si un élément n'est pas dans le tableau de tri, le mettre à la fin
        if (indexA === -1 && indexB === -1) return 0
        if (indexA === -1) return 1
        if (indexB === -1) return -1
        return indexA - indexB
    })
    return sortedDilutions.join(', ')
}

const fetchCapsules = async (params = {}) => {
    const {
        page = 1,
        pageSize = 50,
        search = '',
        statusFilter = 'all',
        sorting = [{ id: 'id', desc: true }]
    } = params;

    // Build query parameters
    const queryParams = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        search: search,
        statusFilter: statusFilter,
        sorting: JSON.stringify(sorting)
    });

    const response = await api.get(`${API.ADMIN_ALL_CAPSULES}?${queryParams.toString()}`);
    if (response.status === 200) {
        return response.data;
    }
    return { values: [], total: 0, page: 1, pageSize: 50, pageCount: 0 };
};

// Memoized ActionCell component
const ActionCell = memo(({
    row,
    navigate,
    handleNavigateToEncode
}) => {
    // Prepare tooltip data directly from row
    const tooltipData = {
        id: row.original.id,
        customer: `${row.original.customer?.firstname || ''} ${row.original.customer?.lastname || ''}`,
        capsule_name: row.original.name || '',
        dilutions: row.original.configurations && row.original.configurations[0] && row.original.configurations[0].configuration ?
            row.original.configurations[0].configuration.content.reduce((acc, item) => {
                const dilutionLabel = item?.dilution?.label || item?.mixture?.defaultDilution?.label;
                if (dilutionLabel && !acc.includes(dilutionLabel)) {
                    acc.push(dilutionLabel);
                }
                return acc;
            }, []) : [],
        creation_date: row.original.creationDate
    };

    return (
        <div className="z-50 relative flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {/* Boutons d'action individuels */}
            <button
                className="btn btn-ghost btn-sm btn-circle"
                title="Modifier"
                onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/clients/${row.original.customerId}/capsule/${row.original.id}`, { state: { fromAdmin: true } });
                }}
            >
                <PencilIcon className="w-5 h-5 text-gray-500 hover:text-primary transition-all" />
            </button>
            <button
                className="btn btn-ghost btn-sm btn-circle"
                title="Encodage"
                onClick={handleNavigateToEncode(row.original.id)}
            >
                <ClipboardDocumentListIcon className="w-5 h-5 text-gray-500 hover:text-primary transition-all" />
            </button>

            {/* Popover PDF avec Radix UI */}
            <RadixPopover
                content={
                    <div className="p-4 bg-base-100 w-80">
                        <TooltipLabelPDF capsule={tooltipData} />
                    </div>
                }
                side="top"
                align="center"
                sideOffset={10}
                contentClassName="p-0 bg-transparent border-none shadow-none"
            >
                <button
                    className="btn btn-ghost btn-sm btn-circle"
                    title="Générer PDF"
                >
                    <DocumentTextIcon className="w-5 h-5 text-gray-500 hover:text-primary transition-all" />
                </button>
            </RadixPopover>
        </div>
    );
}, (prevProps, nextProps) => {
    // Only re-render if these specific props change
    return (
        prevProps.row.original.id === nextProps.row.original.id
    );
});

const CapsulesManagement = () => {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedRows, setExpandedRows] = useState({});
    const [sorting, setSorting] = useState([{
        id: 'id',
        desc: true
    }]);
    const [tableContext, setTableContext] = useState(null);

    // État pour le filtrage par statut de commande
    const [statusFilter, setStatusFilter] = useState('with-order'); // Par défaut : capsules avec commande

    // Pagination state
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 50,
    });

    // Debounce search query to avoid too many API calls
    const debouncedSearchQuery = useDebounce(searchQuery, 300);

    // Query for fetching capsules with server-side pagination, filtering and sorting
    const {
        data,
        error,
        isLoading,
        isError,
        refetch,
        isFetching
    } = useQuery({
        queryKey: ['capsules', pagination.pageIndex + 1, pagination.pageSize, debouncedSearchQuery, statusFilter, sorting],
        queryFn: () => fetchCapsules({
            page: pagination.pageIndex + 1, // Convert to 1-based for server
            pageSize: pagination.pageSize,
            search: debouncedSearchQuery,
            statusFilter: statusFilter,
            sorting: sorting
        }),
        keepPreviousData: true, // Keep previous data while fetching new data
        staleTime: 30000, // Consider data fresh for 30 seconds
        cacheTime: 5 * 60 * 1000, // Keep cache for 5 minutes
    });

    // Use server data directly (no client-side filtering needed)
    const capsules = data?.values || [];
    const total = data?.total || 0;
    const pageCount = data?.pageCount || 0;

    // Calculate current page data (should be the same as server data)
    const currentPageData = useMemo(() => {
        return capsules;
    }, [capsules]);

    // Reset pagination when filters change
    useEffect(() => {
        setPagination(prev => ({ ...prev, pageIndex: 0 }));
    }, [debouncedSearchQuery, statusFilter]);

    // Create a mock table object for the Pagination component
    const mockTable = useMemo(() => ({
        getPageCount: () => pageCount,
        setPageIndex: (index) => {
            setPagination(prev => ({ ...prev, pageIndex: index }));
        },
        setPageSize: (size) => setPagination(prev => ({ ...prev, pageSize: size, pageIndex: 0 })),
        nextPage: () => setPagination(prev => ({ ...prev, pageIndex: Math.min(prev.pageIndex + 1, pageCount - 1) })),
        previousPage: () => setPagination(prev => ({ ...prev, pageIndex: Math.max(prev.pageIndex - 1, 0) })),
        getRowCount: () => capsules?.length || 0
    }), [pageCount, capsules]);

    // Memoized handlers to prevent unnecessary re-renders
    const handleSearchChange = useCallback((e) => {
        setSearchQuery(e.target.value);
    }, []);

    const handleStatusFilterChange = useCallback((e) => {
        setStatusFilter(e.target.value);
    }, []);

    const handleResetFilters = useCallback(() => {
        setSearchQuery('');
        setStatusFilter('all');
    }, []);

    const handleSortingChange = useCallback((newSorting) => {
        setSorting(newSorting);
    }, []);

    // Function to toggle row expansion
    const toggleRowExpanded = (id) => {
        setExpandedRows(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    // Function to render expanded content
    const renderExpandedContent = (capsule) => {
        if (!expandedRows[capsule.id]) return null;
        return (
            <div className="py-6 px-8 bg-base-200 border-t border-base-300 rounded-b-lg shadow-inner mb-4">
                <CapsuleDetails capsule={capsule} />
            </div>
        );
    };

    const tableRef = useRef(null);
    const tableActionsRef = useRef(null);
    // Effect to scroll to top when pagination changes
    useEffect(() => {
        if (tableRef.current) {
            const yOffset = -50; // Add a -50px offset
            const element = tableActionsRef.current;
            const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

            window.scrollTo({
                top: y,
                behavior: 'smooth'
            });
        }
    }, [pagination.pageIndex]);

    // Function to navigate to encode page
    const handleNavigateToEncode = (id) => (e) => {
        e.stopPropagation();
        window.open(`/encode/capsules/${id}`, '_blank');
    };

    const columns = [
        {
            header: '',
            id: 'expander',
            cell: ({ row }) => (
                <button
                    className="btn btn-ghost btn-sm btn-circle"
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleRowExpanded(row.original.id);
                    }}
                >
                    {expandedRows[row.original.id] ? (
                        <ChevronDownIcon className="w-5 h-5 text-gray-500" />
                    ) : (
                        <ChevronRightIcon className="w-5 h-5 text-gray-500" />
                    )}
                </button>
            ),
            size: 40,
        },
        {
            header: 'Actions',
            enableSorting: false,
            cell: ({ row }) => (
                <ActionCell
                    row={row}
                    navigate={navigate}
                    handleNavigateToEncode={handleNavigateToEncode}
                />
            ),
        },
        {
            header: 'ID',
            accessorKey: 'id',
            sortingFn: 'basic',
            cell: ({ row }) => <span className='font-bold'>{row.original.id}</span>,
        },
        {
            header: 'Capsule',
            accessorKey: 'name',
            cell: ({ row }) => <span className={`${row?.original?.mixture?.category === 'Effluences' ? 'text-accent' : ''}`}>{row.original.name}</span>,
            sortingFn: 'text',
        },
        {
            header: 'ID Woo.',
            accessorKey: 'latestOrderId',
            sortingFn: 'basic',
            cell: ({ row }) => {
                const latestOrderId = row.original?.latestOrderId;
                if (!latestOrderId) return '-';

                const value = '#' + latestOrderId;
                const link = 'https://www.lumen-care.com/wp-admin/post.php?post=' + latestOrderId + '&action=edit';

                return (
                    <a
                        href={link}
                        target='_blank'
                        className='font-bold z-50 underline hover:text-primary transition-colors'
                        onClick={(e) => e.stopPropagation()}
                    >
                        {value}
                    </a>
                );
            },
        },
        {
            header: 'Client',
            accessorKey: 'customer',
            sortingFn: 'text',
            cell: ({ row }) => `${row.original.customer?.firstname || ''} ${row.original.customer?.lastname || ''}`,
        },
        {
            header: 'Dilutions',
            accessorKey: 'dilutions',
            sortingFn: 'text',
            cell: ({ row }) => {
                const configurations = row.original.configurations;
                if (!configurations || configurations.length === 0 || !configurations[0].configuration) {
                    return '-';
                }
                const dilutions = configurations[0].configuration.content.map(item =>
                    item.dilution?.label || item.mixture?.defaultDilution?.label
                ).filter(Boolean);
                return sortDilutions(dilutions);
            },
        },
        {
            header: 'Auteur',
            accessorKey: 'user',
            sortingFn: 'text',
            cell: ({ row }) => `${row.original.user?.firstname || ''} ${row.original.user?.lastname || ''}`,
        },
        {
            header: 'Créé le',
            accessorKey: 'creationDate',
            sortingFn: 'datetime',
            cell: ({ row }) => formatDate(row.original.creationDate, true),
        },
        {
            header: 'Statut',
            accessorKey: 'status',
            enableSorting: false,
            cell: ({ row }) => {
                const orders = row.original.orders || [];
                const orderCount = row.original.orderCount || orders.length;
                const priorityStatus = row.original.priorityStatus || row.original.status;

                return (
                    <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-2">
                        <OrderStatus status={priorityStatus} />
                        {orderCount > 1 && (
                            <RadixPopover
                                content={
                                    <div className="p-3 bg-base-100 min-w-48">
                                        <div className="font-semibold mb-2">Commandes ({orderCount})</div>
                                        {orders.map(order => (
                                            <div key={order.id} className="flex justify-between items-center py-1">
                                                <OrderStatus status={order.status} size="sm" />
                                                <a
                                                    href={`https://www.lumen-care.com/wp-admin/post.php?post=${order.wcId}&action=edit`}
                                                    target="_blank"
                                                    className="text-primary hover:text-primary-focus underline text-sm"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    #{order.wcId}
                                                </a>
                                            </div>
                                        ))}
                                    </div>
                                }
                                side="top"
                                align="center"
                            >
                                <span className="badge badge-info badge-sm cursor-help">
                                    {orderCount} cmd
                                </span>
                            </RadixPopover>
                        )}
                    </div>
                );
            },
            size: 300,
        },
        {
            header: 'Remarques',
            accessorKey: 'information',
            sortingFn: 'text',
            cell: ({ row }) => row.original.information,
        },
    ];

    if (isError) {
        return (
            <div className="space-y-4">
                <div className="flex justify-between items-center">
                    <h2 className="text-2xl font-bold">Gestion des Capsules</h2>
                </div>
                <div className="alert alert-error">
                    <div>
                        <h3 className="font-bold">Erreur de chargement</h3>
                        <div className="text-sm">{error.message}</div>
                    </div>
                    <button
                        className="btn btn-sm btn-outline"
                        onClick={() => refetch()}
                    >
                        Réessayer
                    </button>
                </div>
            </div>
        );
    }


    return (
        <div className="space-y-6">


            {/* Controls Section */}
            <div className="bg-base-100 rounded-lg border border-base-300 p-4 relative">
                <div ref={tableActionsRef} className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 flex-wrap">
                    {/* Search and Filters */}
                    <div className="flex flex-col flex-wrap sm:flex-row gap-3 flex-1">
                        <div className="relative flex-1 min-w-72 max-w-md">
                            <input
                                type="text"
                                placeholder="Rechercher par Id, Nom, Client ou Thérapeute"
                                className="input input-bordered w-full pr-10"
                                value={searchQuery}
                                onChange={handleSearchChange}
                            />
                            {isFetching && searchQuery && (
                                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                                </div>
                            )}
                        </div>

                        <div className="flex items-center gap-2">
                            <select
                                className="select select-bordered select-primary select-sm"
                                value={statusFilter}
                                onChange={handleStatusFilterChange}
                            >
                                <option value="all">Tous les statuts</option>
                                <option value="with-order">Avec commande</option>
                                <option value="without-order">Sans commande</option>
                                <option value="multiple-orders">Multiples commandes</option>
                                <optgroup label="Statuts spécifiques">
                                    <option value="pending">En attente</option>
                                    <option value="processing">En cours</option>
                                    <option value="on-hold">En attente de vérification</option>
                                    <option value="completed">Terminé</option>
                                    <option value="cancelled">Annulé</option>
                                    <option value="refunded">Remboursé</option>
                                    <option value="failed">Échoué</option>
                                    <option value="trash">Corbeille</option>
                                    <option value="livre">Livrée</option>
                                    <option value="expedie">Envoyée</option>
                                </optgroup>
                            </select>

                            {(searchQuery || statusFilter !== 'all') && (
                                <button
                                    className="btn btn-sm btn-outline btn-primary"
                                    onClick={handleResetFilters}
                                    title="Réinitialiser les filtres"
                                >
                                    Réinitialiser
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="text-sm text-base-content/70">
                        <div className="flex items-center gap-2">
                            <span className="font-medium">{total.toLocaleString()}</span>
                            <span>résultats</span>
                            {statusFilter !== 'all' && (
                                <span className="badge badge-primary badge-sm">
                                    {getStatusFilter(statusFilter)}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Pagination and Results Info */}

                    <Pagination
                        server={{
                            total: total,
                            fetched: capsules?.length || 0,
                        }}
                        client={pagination}
                        table={tableContext || mockTable}
                    />

                </div>

            </div>

            {/* Table Section */}
            <div className="relative rounded-lg shadow-sm border border-base-300 bg-base-100" ref={tableRef}>
                <div className="overflow-x-auto">
                    {/* Loading overlay for table only */}
                    {(isLoading) && (
                        <div className="absolute inset-0  backdrop-brightness-95 backdrop-blur-xs z-20 flex items-center justify-center">
                            <div className={`flex flex-col self-start ${capsules.length > 0 ? 'mt-[25%]' : 'mt-[12%]'} justify-start items-center gap-3 `}>
                                <Loader className="w-8 h-8 animate-spin text-primary" />
                                <span className="text-primary">Chargement des capsules...</span>
                            </div>
                        </div>
                    )}

                    {capsules.length === 0 && !isLoading && (
                        <div className="text-center py-12">
                            <div className="text-base-content/60">
                                {searchQuery || statusFilter !== 'all' ?
                                    'Aucune capsule trouvée avec les critères sélectionnés' :
                                    'Aucune capsule disponible'
                                }
                            </div>
                        </div>
                    )}
                    {capsules.length > 0 && (
                        <TanstackTable
                            data={currentPageData}
                            columns={columns}
                            onRowClick={(row) => toggleRowExpanded(row.original.id)}
                            sorting={sorting}
                            setSorting={handleSortingChange}
                            getRowClassName={(row) => expandedRows[row.id] ? 'bg-base-200 border-b-0 ' : ''}
                            renderExpandedContent={renderExpandedContent}
                        />
                    )}
                    <Pagination
                        server={{
                            total: total,
                            fetched: capsules?.length || 0,
                        }}
                        client={pagination}
                        table={tableContext || mockTable}
                    />
                </div>
            </div>
        </div>
    );
};

export default CapsulesManagement;